/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Button } from 'button-mui'
import { AddIcon } from 'pdc-svg-icons'
import DropdownMenu from 'dropdown-menu'
import { getVerbalColors } from 'helpers'
import { ScreenSizeContext } from 'providers'
import { DefaultArrowTooltip } from 'tooltips'
import { getValue } from 'remote-config-value'
import { makeStyles } from '@material-ui/core/styles'
import ConfirmDeleteModal from './ConfirmDeleteModal'

import StatusChip from './StatusChip'
import BrandsPreview from './brands/Preview'
import NumberSelectStep from './NumberSelectStep'
import Policies from './campaigns/steps/Policies'
import CampaignsPreview from './campaigns/Preview'
import BrandsLandingPage from './brands/LandingPage'
import NumberCampaignStep from './NumberCampaignStep'
import NumbersLandingPage from './NumbersLandingPage'
import StepWizard from './StepWizardClone/WizardClone'
import CampaignUsageStep from './campaigns/steps/Usage'
import CampaignsLandingPage from './campaigns/LandingPage'
import CampaignContactStep from './campaigns/steps/Contact'
import CampaignPreviewStep from './campaigns/steps/Preview'
import CampaignPaymentStep from './campaigns/steps/Payment'
import { EnrollmentType } from '../interface/EnrollmentType'
import { Table, Icon } from 'foundations-library/components'
import SampleMessages from './campaigns/steps/SampleMessages'
import { CampaignContext } from '../contexts/CampaignContext'
import BusinessPaymentStep from './brands/steps/BusinessPaymentStep'
import CampaignSubmittedDialog from './campaigns/CampaignSubmittedDialog'
import BusinessInformationStep from './brands/steps/BusinessInformationStep'
import BusinessAddressAndContact from './brands/steps/BusinessAddressAndContact'
import { failedStatuses } from '../utils/statuses'

const styles = makeStyles((theme: any) => {
    const { colors } = theme['foundations-library'] || {}
    const verbalColors = getVerbalColors(colors)
    return {
        stepWizard: {
            height: `calc( 100% - ${theme.overrides.MuiTabs == null ? 0 : theme.overrides.MuiTabs.root.height}px)`,
            paddingBottom: 40,
            '& .stepper-nav-button': { display: 'none' },
            '& .MuiMobileStepper-no-padding': { justifyContent: 'center' }
        },
        container: {
            maxWidth: 1000,
            margin: '0 auto'
        },
        searchContainer: { visibility: 'hidden' },
        editNumberColumn: {
            cursor: 'pointer',
            '& .edit-button': {
                borderRadius: '50%',
                width: 30,
                height: 30,
                minWidth: 30,
                minHeight: 30,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: 'rgb(133 146 154 / 15%)',
                '& > svg': { fontSize: 20, color: '#6E7A82' },
                '&:hover': {
                    backgroundColor: 'rgb(206 239 199 / 50%)',
                    '& > svg': { color: '#29921B' }
                }
            }
        },
        tableWrapper: {
            // width: '1000px',
            margin: '0 auto',
            border: `1px solid ${verbalColors['neutral-100']}`,
            padding: '20px',
            borderRadius: '10px'
        },
        enrollmentTable: {
            tableLayout: 'fixed',
            '& thead th': {
                borderTop: 'none',
                borderBottom: 'none',
                backgroundColor: verbalColors['neutral-50'],
                textTransform: 'none',
                paddingLeft: '10px',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '18px',
                letterSpacing: '0px',
                verticalAlign: 'middle',
                color: verbalColors['neutral-700'],
                width: 'calc((100% - 55px) / 4)',
                '&:first-child': {
                    borderTopLeftRadius: '5px',
                    borderBottomLeftRadius: '5px'
                },
                '&:last-child': {
                    borderTopRightRadius: '5px',
                    borderBottomRightRadius: '5px',
                    width: '55px'
                }
            },
            '& tbody td': {
                paddingLeft: '10px',
                height: '70px',
                width: 'calc((100% - 55px) / 4)',
                color: verbalColors['neutral-700'],
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '21px',
                letterSpacing: '0px'
            },
            '& tbody tr td:last-child': {
                textAlign: 'center',
                verticalAlign: 'middle',
                width: '55px',
                paddingLeft: 0
            },
            '& tbody tr:last-child': {
                boxShadow: 'none'
            }
        },
        brandActionsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '40px'
        },
        deleteMenuItem: {
            color: '#F5472C !important'
        }
    }
})

const getTooltipText = (disableDelete, isNotRejected, hasDIDs, isDeleted) => {
    if (!disableDelete) return null
    if (isNotRejected) return getValue('sms_enrollment_campaign_delete_not_rejected_tooltip')
    if (hasDIDs) return getValue('sms_enrollment_campaign_delete_tooltip')
    if (isDeleted) return getValue('sms_enrollment_enrollment_status_deleted_tooltip')

    return null
}

interface Props {
    enrollmentType: EnrollmentType
    itemType: string
    acknowledgedTitle: string

    getDeleteParams: (object) => Promise<Record<string, unknown>>
    deleteEnrollment: (any) => void
    deleteEnrollmentFeature: boolean

    getAddParams: (object) => Promise<Record<string, unknown>>
    addEnrollment: (params: any) => void

    getEditParams: (stepWizardData, editing) => Promise<Record<string, unknown>>
    editDisabledHelper?: (enrollment) => {
        editDisabled: boolean
        editTooltip: string | null
    }
    editEnrollment: (parms: any) => void
    includeEditInMenu?: (params: any) => boolean
    editEnrollmentFeature?: boolean

    previewEnrollmentFeature?: boolean

    networkError: any
    setNetworkError: (value) => void

    enrollmentEnabled: boolean
    enrollmentDisabledTooltip: string

    enrollments: Array<any>
    newEnrollmentRemoteID: string
    tableHeadColumns: Array<any>
    onActivePage?: (activePage) => unknown
    forceShowListPage?: boolean
    onStartSubmit?: () => void
    onStartDelete?: () => void
    networkPending?: boolean
    onChangeTab?(v, c): unknown
    forceShowWizard?: boolean

}
/**
 *
 */
const Enrollment = ({
    enrollmentType,
    itemType,
    acknowledgedTitle,
    getDeleteParams,
    deleteEnrollment,
    getAddParams,
    getEditParams,
    addEnrollment,
    editEnrollment,
    deleteEnrollmentFeature,
    previewEnrollmentFeature = false,
    editEnrollmentFeature = false,
    networkError,
    setNetworkError,
    editDisabledHelper = () => ({ editDisabled: false, editTooltip: null }),
    enrollmentEnabled,
    enrollmentDisabledTooltip,
    includeEditInMenu = () => true,
    enrollments,
    newEnrollmentRemoteID,
    tableHeadColumns,
    onActivePage,
    forceShowListPage = false,
    onStartSubmit,
    networkPending = false,
    onStartDelete,
    onChangeTab,
    forceShowWizard = false
}: Props) => {
    const {
        acceptedCampaigns
    } = useContext(CampaignContext)

    const screenSizeContext = useContext(ScreenSizeContext)
    const isTabletSize = screenSizeContext.tablet

    const getValueByEnrollmentType = (brandValue, campaignValue, smsNumberValue) => {
        return (
            enrollmentType === EnrollmentType.Brand
                ? brandValue
                : enrollmentType === EnrollmentType.Campaign
                    ? campaignValue
                    : enrollmentType === EnrollmentType.SmsNumber
                        ? smsNumberValue
                        : null
        )
    }
    const classes = styles()
    const [editing, setEditing] = useState(null)
    const [preview, setPreview] = useState(null)
    const [deleting, setDeleting] = useState(null)
    const [forceActiveStep, setForceActiveStep] = useState(null)
    const [showWizard, setShowWizard] = useState(forceShowWizard)
    const [submitPhase, setSubmitPhase] = useState<'idle' | 'waiting' | 'pending'>('idle')
    const [isDeleteStarting, setIsDeleteStarting] = useState(false)
    const [showCampaignSubmittedDialog, setShowCampaignSubmittedDialog] = useState(false)

    useEffect(() => {
        if (forceShowListPage) {
            setShowWizard(false)
            setPreview(null)
            setEditing(null)
        }
    }, [forceShowListPage])

    const brandSteps = [
        {
            id: 'business_info',
            label: getValue('sms_enrollment_business_information'),
            component: BusinessInformationStep,
            additionalProps: {
                editing: editing,
                mode: editing ? 'editing' : 'creating'
            }
        },
        {
            id: 'business_address',
            label: getValue('sms_enrollment_address_and_contact'),
            component: BusinessAddressAndContact,
            additionalProps: {
                editing: editing,
                mode: editing ? 'editing' : 'creating'
            }
        },
        {
            id: 'business_confirm',
            label: getValue('sms_enrollment_details'),
            component: BusinessPaymentStep,
            additionalProps: {
                showAlert: true,
                showConfirmation: true,
                editing,
                mode: editing ? 'editing' : 'creating',
                onCancel: () => setShowWizard(false)
            }
        }
    ]
    const campaignSteps = useMemo(
        () => {
            return [
                {
                    id: 'campaign_usage',
                    label: getValue('sms_enrollment_campaigns_1st_step_title'),
                    component: CampaignUsageStep,
                    additionalProps: {
                        editing,
                        mode: editing ? 'editing' : 'creating'
                    }
                },
                {
                    id: 'campaign_sms_content',
                    label: getValue('sms_enrollment_campaigns_2nd_step_title'),
                    title: getValue('sms_enrollment_sample_message'),
                    component: SampleMessages,
                    additionalProps: {
                        editing,
                        mode: editing ? 'editing' : 'creating'
                    }
                },
                {
                    id: 'campaign_policies',
                    label: getValue('sms_enrollment_campaigns_3rd_step_title'),
                    title: getValue('sms_enrollment_content_and_messaging'),
                    component: Policies,
                    additionalProps: {
                        editing,
                        mode: editing ? 'editing' : 'creating'
                    }
                },
                {
                    id: 'campaign_contact',
                    label: getValue('sms_enrollment_campaigns_4th_step_title'),
                    title: getValue('sms_enrollment_contact_information'),
                    component: CampaignContactStep,
                    additionalProps: {
                        editing,
                        mode: editing ? 'editing' : 'creating'
                    }
                },
                {
                    id: 'campaign_preview',
                    label: getValue('sms_enrollment_campaigns_5th_step_title'),
                    secondaryLabel: enrollmentType === EnrollmentType.Campaign && isTabletSize ? null : getValue('sms_enrollment_information'),
                    component: CampaignPreviewStep,
                    additionalProps: {
                        editing,
                        mode: editing ? 'editing' : 'creating'
                    }
                },
                {
                    id: 'campaign_invoice',
                    label: getValue('sms_enrollment_campaigns_6th_step_title'),
                    component: CampaignPaymentStep,
                    additionalProps: {
                        showAlert: true,
                        showConfirmation: true,
                        editing,
                        mode: editing ? 'editing' : 'creating',
                        onCancel: () => setShowWizard(false)
                    }
                }
            ]
        },
        [isTabletSize, editing]
    )
    const smsNumberSteps = [
        {
            id: 'campaign_step',
            label: getValue('sms_enrollment_campaign_tab_title'),
            secondaryLabel: getValue('sms_enrollment_number_step_1_secondaryLabel'),
            title: getValue('sms_enrollment_number_step_1_title'),
            component: NumberCampaignStep,
            additionalProps: {
                eligibleCampaigns: acceptedCampaigns
            }
        },
        {
            id: 'number_step',
            label: getValue('sms_enrollment_sms_number_column_name'),
            secondaryLabel: getValue('sms_enrollment_number_step_2_secondaryLabel'),
            title: getValue('sms_enrollment_number_step_2_title'),
            component: NumberSelectStep
        }
    ]
    const steps = getValueByEnrollmentType(brandSteps, campaignSteps, smsNumberSteps)

    const optionsDropdownButton = (props) => {
        return (
            <div {...props}>
                <Icon name="more-vert" size={24} data-button="options" color={'#364047'} />
            </div>
        )
    }
    const renderRegisteredOnColumn = (row) => {
        const createdDate = getValueByEnrollmentType(row.created_date, row.created_at, null)
        const d = new Date(0)
        d.setUTCSeconds(createdDate)

        const year = d.toLocaleString('default', { year: 'numeric' })
        const month = d.toLocaleString('default', { month: '2-digit' })
        const day = d.toLocaleString('default', { day: '2-digit' })

        return `${year}-${month}-${day}`
    }
    const getBrandMenuItems = (row) => {
        const hasCampaigns = row.campaign_count > 0
        const isDeleted = row.deleted_at != null
        const disableDelete = hasCampaigns || isDeleted
        const menuItems = []
        if (deleteEnrollmentFeature) {
            const tooltipText = disableDelete ? hasCampaigns ? getValue('sms_enrollment_brand_delete_tooltip') : isDeleted ? getValue('sms_enrollment_enrollment_status_deleted_tooltip') : null : null
            menuItems.push({
                text: 'Delete',
                onClick: () => setDeleting(row),
                disabled: disableDelete,
                tooltipText: tooltipText,
                'data-testid': `item-delete-${row.csp_brand_id}`
            })
        }

        return menuItems
    }
    const getCampaignMenuItems = (row) => {
        const hasDIDs = row.active_did_count > 0
        const isDeleted = row.deleted_at != null
        const isNotRejected = !row.status || !failedStatuses.includes(row.status)
        const disableDelete = hasDIDs || isDeleted || isNotRejected
        const menuItems = []
        if (deleteEnrollmentFeature) {
            const tooltipText = getTooltipText(disableDelete, isNotRejected, hasDIDs, isDeleted)
            menuItems.push(
                {
                    text: 'Delete',
                    onClick: () => setDeleting(row),
                    disabled: disableDelete,
                    tooltipText: tooltipText,
                    'data-testid': `item-delete-${row.csp_campaign_id}`
                })
        }
        return menuItems
    }
    const getSmsNumberMenuItems = (row) => {
        const menuItems = []
        if (deleteEnrollmentFeature) {
            menuItems.push({
                text: 'Delete',
                onClick: () => {
                    setDeleting(row)
                },
                'data-testid': `item-delete-${row.voip_did_id}`
            })
        }
        return menuItems
    }
    const renderOptionsColumn = (row) => {
        const menuItems: {
            text: string
            onClick: () => void
            disabled?: boolean
            tooltipText?: string | null
            color?: string
            'data-testid': string
        }[] = []

        const testUniqueIdentifier = row.csp_campaign_id || row.csp_brand_id || row.voip_did_id

        if (editEnrollmentFeature || includeEditInMenu(row)) {
            const { editDisabled, editTooltip } = editDisabledHelper(row)

            menuItems.push({
                text: 'Review/Edit',
                onClick: () => setPreview(row),
                disabled: editDisabled,
                tooltipText: editDisabled ? editTooltip : null,
                'data-testid': `item-preview-${testUniqueIdentifier}`
            })
        }

        const deleteMenuItem = getValueByEnrollmentType(
            getBrandMenuItems(row),
            getCampaignMenuItems(row),
            getSmsNumberMenuItems(row)
        )?.[0]

        if (deleteMenuItem) {
            menuItems.push({
                ...deleteMenuItem,
                text: 'Delete',
                className: classes.deleteMenuItem,
                'data-testid': `item-delete-${testUniqueIdentifier}`
            })
        }

        return menuItems.length > 0
            ? (
                <DropdownMenu
                    button={optionsDropdownButton}
                    menuItems={menuItems}
                    dropdownStyle='configure'
                    data-testid={`item-drop-down-${testUniqueIdentifier}`}
                />
            )
            : <></>
    }
    const generateOneRow = (data) => {
        const brandColumns = [
            data.csp_brand_id,
            data.name,
            renderRegisteredOnColumn(data),
            <StatusChip
                latestEvent={data.latest_event}
                status={data.registration_status}
                enrollmentType={EnrollmentType.Brand}
                deleted={data.deleted_at != null}
                itemsAttached={data.campaign_count}
                key={data.csp_brand_id}
                data-testid={`chip-${data.csp_brand_id}`}
            />,
            renderOptionsColumn(data)
        ]

        const campaignColumns = [
            data.name,
            data.brand_name,
            renderRegisteredOnColumn(data),
            <StatusChip
                status={data.status}
                enrollmentType={EnrollmentType.Campaign}
                deleted={data.deleted_at != null}
                itemsAttached={data.active_did_count}
                key={data.csp_campaign_id}
                data-testid={`chip-${data.csp_campaign_id}`}
            />,
            renderOptionsColumn(data)
        ]
        const smsNumberColumns = [
            data['csp_campaign.name'],
            data.did, // data.number
            renderOptionsColumn(data)
        ]

        const columns = getValueByEnrollmentType(brandColumns, campaignColumns, smsNumberColumns)

        return { key: data.id, columns }
    }
    const generateTableRows = () => {
        const rows = !(enrollments?.length > 0 && typeof enrollments?.map === 'function')
            ? []
            : enrollments.map(x => {
                return generateOneRow(x)
            })
        return rows
    }

    const onClickHandler = async () => {
        setShowWizard(true)
    }

    const onSubmitHandler = async (data) => {
        setSubmitPhase('waiting')
        onStartSubmit?.()
        if (editing == null) {
            const addParams = await getAddParams(data)
            addEnrollment(addParams)
        } else {
            const editParams = await getEditParams(data, editing)
            editEnrollment(editParams)
        }
    }

    const onDeleteHandler = async () => {
        setIsDeleteStarting(true)
        onStartDelete?.()
        const deleteParms = await getDeleteParams(deleting)
        deleteEnrollment(deleteParms)
    }
    useEffect(() => {
        if (editing != null) {
            setShowWizard(true)
        }
    }, [editing])

    const smallView = enrollmentType === EnrollmentType.Campaign && screenSizeContext.mobile

    useEffect(() => {
        const activePage = (preview || editing) ? 'show' : showWizard ? 'create' : 'list'
        onActivePage?.(activePage)
    }, [showWizard, editing, preview])

    useEffect(() => {
        if (submitPhase === 'waiting' && networkPending) {
            setSubmitPhase('pending')
        }
    }, [submitPhase, networkPending])

    useEffect(() => {
        if (submitPhase === 'pending' && !networkPending) {
            if (!networkError) {
                if (enrollmentType === EnrollmentType.Campaign) {
                    setShowCampaignSubmittedDialog(true)
                } else {
                    setShowWizard(false)
                }
            }
            setSubmitPhase('idle')
        }
    }, [submitPhase, networkPending, networkError])

    useEffect(() => {
        if (isDeleteStarting && networkPending) {
            setDeleting(null)
            setIsDeleteStarting(false)
        }
    }, [isDeleteStarting, networkPending])

    useEffect(() => {
        if (isDeleteStarting && !networkPending && networkError) {
            setDeleting(null)
            setIsDeleteStarting(false)
        }
    }, [isDeleteStarting, networkPending, networkError])

    const setEditingRemovePreview = () => {
        setEditing({ ...preview })
        setPreview(null)
    }

    const onClickSubmitPaymentFailed = () => {
        setForceActiveStep(
            getValueByEnrollmentType(
                brandSteps[brandSteps.length - 1].id,
                campaignSteps[campaignSteps.length - 1].id,
                null
            )
        )
        setEditingRemovePreview()
        setShowWizard(true)
        setTimeout(() => { // cleanup
            setForceActiveStep(null)
        }, 100)
    }

    const renderContent = () => {
        if (preview) {
            if (enrollmentType === EnrollmentType.Brand) {
                return <BrandsPreview
                    item={preview}
                    onClickEdit={setEditingRemovePreview}
                    onClickSubmitPaymentFailed={onClickSubmitPaymentFailed}
                    onGoBack={() => setPreview(null)}
                />
            }
            if (enrollmentType === EnrollmentType.Campaign) {
                return <CampaignsPreview
                    item={preview}
                    onClickEdit={setEditingRemovePreview}
                    onClickSubmitPaymentFailed={onClickSubmitPaymentFailed}
                    onGoBack={() => setPreview(null)}
                />
            }
        }

        const onRedirectToNumberAssignement = () => {
            setShowCampaignSubmittedDialog(false)
            onChangeTab?.(EnrollmentType.SmsNumber, true)
        }

        if (showWizard) {
            return <div>
                {
                    enrollmentType === EnrollmentType.Campaign
                        ? <CampaignSubmittedDialog
                            isOpen={showCampaignSubmittedDialog && !!onChangeTab}
                            onContinue={onRedirectToNumberAssignement}
                        />
                        : <></>
                }
                <StepWizard
                    className={classes.stepWizard}
                    steps={steps}
                    smallView={smallView}
                    onSubmit={onSubmitHandler}
                    error={networkError ? 'Something went wrong' : null}
                    initialActiveStep={forceActiveStep}
                    onCloseError={() => setNetworkError(null)}
                />
            </div>
        }

        if (!enrollments.length) {
            if (enrollmentType === EnrollmentType.Brand) {
                return <BrandsLandingPage onClickAddNew={onClickHandler} />
            }
            if (enrollmentType === EnrollmentType.Campaign) {
                return <CampaignsLandingPage onClickAddNew={onClickHandler} />
            }

            if (enrollmentType === EnrollmentType.SmsNumber) {
                return <NumbersLandingPage onClickAddNew={onClickHandler} />
            }
        }

        return <div data-testid='sms-tabs-list'>
            <div className={classes.tableWrapper}>
                <div className={classes.brandActionsContainer}>
                    <button className={classes.searchContainer}>Search?</button>
                    <DefaultArrowTooltip id='addEnrollmentBtnTooltip' title={!enrollmentEnabled ? enrollmentDisabledTooltip : null}>
                        <span>
                            <Button
                                id='addEnrollmentBtn'
                                disabled={!enrollmentEnabled}
                                size='medium'
                                startIcon={<AddIcon />}
                                onClick={onClickHandler}
                            >
                                {getValue(newEnrollmentRemoteID)}
                            </Button>
                        </span>
                    </DefaultArrowTooltip>
                </div>

                <Table
                    columns={tableHeadColumns}
                    rows={generateTableRows().map((x) => x.columns)}
                    data-testid='sms-tabs-list-table'
                />
            </div>
            <ConfirmDeleteModal
                isOpen={deleting != null}
                type={
                    enrollmentType === EnrollmentType.Brand
                        ? 'brand'
                        : enrollmentType === EnrollmentType.Campaign
                            ? 'campaign'
                            : 'number'
                }
                selectedItem={deleting}
                onClose={() => setDeleting(null)}
                onDelete={onDeleteHandler}
            />
        </div>
    }

    return <div className={classes.container} >
        {renderContent()}
    </div>
}

export default Enrollment
